import { createTranslation } from '@choffmeister/i18next'
import { css, SerializedStyles } from '@emotion/core'
import parse from 'html-react-parser'
import * as React from 'react'

import Chevron from '../icons/abstract/chevron.svg'
import SVGIcon from './SvgIcon'

interface TemplateViewsDropwdownProps {
  currentItem: number
  setCurrentItem: React.Dispatch<React.SetStateAction<number>>
  items: { name: string | { en: string; fr?: string }; iconName?: string }[]
  className?: string
  cssStyles?: SerializedStyles
}

const ViewsDropdown: React.FunctionComponent<TemplateViewsDropwdownProps> = ({
  currentItem,
  setCurrentItem,
  items,
  className,
  cssStyles,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  const { useTranslation } = React.useMemo(() => {
    return createTranslation({ items: createTranslationDict(items) })
  }, [items])
  const { t } = useTranslation()

  React.useEffect(() => {
    const checkIfClickedOutside = (e: any) => ref.current && !ref.current.contains(e.target) && setIsOpen(false)
    document.addEventListener('click', checkIfClickedOutside)
    return () => {
      document.removeEventListener('click', checkIfClickedOutside)
    }
  }, [])

  return (
    <div className={`views-dropdown-container ${className}`} css={[containerStyle, cssStyles]} ref={ref}>
      <div className="with-box-shadow" css={selectStyle} onClick={() => setIsOpen(!isOpen)}>
        <div className="subtitle">
          {items[currentItem].iconName && <SVGIcon src={`templates/${items[currentItem].iconName}`} />}{' '}
          {parse(t(`items.${currentItem}.name`))}
        </div>
        {items.length > 1 && <Chevron css={chevronStyle(isOpen)} />}
      </div>
      {items.length > 1 && isOpen && (
        <ul className="with-box-shadow" css={contentStyle}>
          {items.map(({ iconName }, index) => (
            <li
              key={index}
              className="subtitle opacity-on-hover"
              css={[itemStyle, currentItem === index && currentTabStyle(iconName)]}
              onClick={() => {
                setCurrentItem(index)
                setIsOpen(false)
              }}
            >
              {iconName && <SVGIcon src={`templates/${iconName}`} />} {parse(t(`items.${index}.name`))}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const createTranslationDict = (items: TemplateViewsDropwdownProps['items']) => {
  return items.map((item) => ({
    name: typeof item.name === 'string' ? { en: item.name } : item.name,
  }))
}

export default ViewsDropdown

const containerStyle = css`
  position: relative;
  box-sizing: border-box;
  user-select: none;

  .subtitle {
    margin: 0;
  }
`

const selectStyle = css`
  padding: 0.5rem 1rem;
  background: #fff;
  border: 1px solid #efeff0;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  .subtitle {
    display: flex;
    align-items: center;

    svg {
      margin-right: 0.5rem;
    }
  }
`

// for SecondaryNavigationDropdown
export const chevronStyle = (isDropDownOpen: boolean) => css`
  transform: rotate(${isDropDownOpen ? '180deg' : '0'});
  transition: transform 0.3s ease;
`

const contentStyle = css`
  box-sizing: border-box;
  position: absolute;
  background: #fff;
  top: 50px; // select field has height of 42px
  border: 1px solid #efeff0;
  border-radius: 0.5rem;
  width: 100%;
  cursor: pointer;

  z-index: 1;
`

const itemStyle = css`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;

  svg {
    margin-right: 0.5rem;
  }
`

const currentTabStyle = (iconName?: string) => css`
  color: #318eff;

  ${iconName === 'inbox_view_small'
    ? `svg > path:nth-child(2) {
    fill: #318eff;
  }`
    : `svg > path:first-child {
    fill: #318eff;
  }`}
`
